import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {LocalModalComponent} from '~shared/components/elements/local-modal/local-modal.component';
import {NotificationType} from '~core/enum';
import {AuthenticatedUser, CampaiMembership, Customer} from '~core/models';
import {DefaultNotificationService, MembershipService, StorageService} from '~core/services';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {MembershipStatusTranslationsService, TooltipTranslationService} from '~shared/translations';
import {copyToClipboard} from '~core/utils/utility-functions';

/**
 * Handles membership management
 */
@UntilDestroy()
@Component({
  selector: 'app-campai-membership-list',
  templateUrl: './campai-membership-list.component.html',
  styleUrls: ['../../../../customer-overview.component.scss']
})
export class CampaiMembershipListComponent implements OnInit, OnDestroy {
  protected readonly copyToClipboard = copyToClipboard;
  @ViewChild(LocalModalComponent) campaiModal: LocalModalComponent;
  public authenticatedUser: AuthenticatedUser;
  public anim: any = '';
  public loading: boolean = true;
  public currentMembership: Array<CampaiMembership>;
  public disableSubmit: boolean;

  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];
  public modalTitle: string = $localize`:@@modal.heading.transferToCampai:Transfer customer to campai`;

  @Input() public customer: Customer;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public customerOverviewService: CustomerOverViewService,
    public membershipService: MembershipService,
    public membershipStatusTranslations: MembershipStatusTranslationsService,
    public tablesTranslations: TablesTranslations,
    public tooltipTranslations: TooltipTranslationService,
    public notificationService: DefaultNotificationService,
    public storage: StorageService,
  ) {
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this)).subscribe(user => this.authenticatedUser = user);
    this.membershipService.castCurrentMembership.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data !== null) {
          this.currentMembership = [data];
        } else {
          this.currentMembership = data;
        }
      });
    this.columns.push(this.tablesTranslations.i18nEnterDate, this.tablesTranslations.i18nMembershipNr,
      this.tablesTranslations.i18nSection, this.tablesTranslations.i18nStatus);
    this.keys.push(['enterDate', 'membershipNumber', 'inlineSections', 'status']);
  }

  public ngOnInit(): void {
    if (this.authenticatedUser.canTransferToCampai) {
      this.membershipService.getCampaiMembershipStatus(this.customer.id).pipe(untilDestroyed(this))
        .subscribe((status: any) => this.disableSubmit = status.wasSynced);
    } else {
      this.disableSubmit = true;
    }

    this.membershipService.getCampaiMembership(this.customer.id).pipe(untilDestroyed(this))
      .subscribe({
        next: res => this.membershipService.announceCurrentMembership(res),
        error: err => this.loading = false,
        complete: () => this.loading = false,
      });
  }

  public ngOnDestroy(): void {
    this.membershipService.clearCurrentMembership();
  }

  /**
   * Open the edit membership modal and pass the address data to it.
   */
  public editMembership(membership: CampaiMembership): any {
    this.customerOverviewService.toggleAllModal(false);
    this.customerOverviewService.toggleEditCampaiMembershipModal(true);
    this.membershipService.announceCurrentMembership(membership);
  }


  public createMembership(): any {
    this.campaiModal.setSpinner(true);
    this.membershipService.createCampaiMembership(this.customer.id).pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.disableSubmit = true;
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: this.membershipStatusTranslations.i18nCampaiTransferInitiated
          });
          this.campaiModal.setModalStatus(false);
          this.campaiModal.setSpinner(false);
        },
        error: (error: Error) => {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.membershipStatusTranslations.i18nCampaiTransferFailed
          });
          this.campaiModal.setModalStatus(false);
          this.campaiModal.setSpinner(false);
        }
      });
  }

  /**
   * Shows the transfer membership modal.
   */
  public transferMembershipModal(): any {
    this.campaiModal.setModalStatus(true);
  }

  /**
   * Function that executes the delete membership process
   */
  public deleteMembershipModal(actionButton: any): void {
    this.customerOverviewService.toggleDeleteModal(true, 'membership', actionButton.data.id);
  }

  public copyCampaiID(): void {
    copyToClipboard(this.customer.externalId);
    this.notificationService.showNotification({
      type: NotificationType.SUCCESS,
      message: this.membershipStatusTranslations.i18nCampaiIdCopied
    });
    this.campaiModal.setModalStatus(false);
  }

  /**
   * Deletes the selected membership
   */
  public deleteMembershipData(membershipId: number): any {
    this.membershipService.delete(membershipId).pipe(untilDestroyed(this))
      .subscribe(() => {
        this.customerOverviewService.toggleLoadingSubmitClicked(false);
        this.customerOverviewService.toggleDeleteModal(false, '', null);
        this.customerOverviewService.toggleAllModal(false);
        this.membershipService.clearCurrentMembership();
      });
  }
}
